import React from 'react';
import { Box, Card, Typography, Button, Dialog, DialogContent, DialogActions, LinearProgress, Chip } from '@mui/material';
import { Bolt as BoltIcon, Close as CloseIcon } from '@mui/icons-material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTier } from '../hooks/useTier';
import { cyberpunkCardSx } from './design-system';
import { track } from '../utils/track';

interface UpgradeNudgeProps {
  variant: 'inline' | 'banner' | 'modal' | 'freeProDay';
  open?: boolean;
  onClose?: () => void;
  usageType?: 'food' | 'analysis' | 'assistant' | 'camera' | 'voice';
  message?: string;
  title?: string;
}

const UpgradeNudge: React.FC<UpgradeNudgeProps> = ({ variant, open, onClose, usageType, message, title }) => {
  const navigate = useNavigate();
  const { dailyUsage, dailyLimits, streakBonus, isFreeProDay, freeProDayExpiresAt } = useTier();

  const src = `nudge_${variant}${usageType ? `_${usageType}` : ''}`;
  // Record when this nudge is actually shown to the user (impression), so we can
  // measure which placements convert. Modal fires on open; inline/banner on mount.
  useEffect(() => {
    if (variant === 'modal') {
      if (open) track('nudge_shown', { variant, usageType });
    } else {
      track('nudge_shown', { variant, usageType });
    }
  }, [variant, usageType, open]);

  const goUpgrade = () => {
    track('upgrade_page_view', { src });
    navigate(`/upgrade?src=${encodeURIComponent(src)}`);
  };

  const freeProLeft = (() => {
    if (!isFreeProDay || !freeProDayExpiresAt) return null;
    const ms = new Date(freeProDayExpiresAt).getTime() - Date.now();
    if (ms <= 0) return null;
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return `${h}h ${m}m`;
  })();

  const getUsageInfo = () => {
    switch (usageType) {
      case 'food':
        return { used: dailyUsage.aiFoodQueries, limit: dailyLimits.aiFoodLookups, label: 'Food lookups' };
      case 'analysis':
        return { used: dailyUsage.aiAnalyses, limit: dailyLimits.aiAnalyses, label: 'Analyses' };
      case 'assistant':
        return { used: dailyUsage.aiAssistantMessages, limit: dailyLimits.aiAssistantMessages, label: 'Questions' };
      case 'camera':
      case 'voice':
        return null; // Pro-only features, no usage meter
      default:
        return null;
    }
  };

  const getFeatureMessage = () => {
    switch (usageType) {
      case 'camera':
        return 'Photograph your meal and every item is logged, every nutrient included. Photo logging comes with Pro.';
      case 'voice':
        return 'Say what you ate and it is logged. Voice logging comes with Pro.';
      default:
        return null;
    }
  };

  const usageInfo = getUsageInfo();
  const featureMessage = getFeatureMessage();
  const defaultMessage = featureMessage
    ? featureMessage
    : usageInfo
    ? `You've used ${usageInfo.used} of ${usageInfo.limit} ${usageInfo.label.toLowerCase()} today.`
    : 'That is today\'s free allowance.';

  const isProFeature = usageType === 'camera' || usageType === 'voice';

  // Free Pro Day nudge — self-gating: renders nothing unless the user is
  // actually on an active free Pro day. Safe to place unconditionally.
  if (variant === 'freeProDay') {
    // Only in the last 6 hours of the Pro day (2026-11-03, Fulcrum #15 + Jason: a "Keep Pro"
    // banner on the first screen of day one asks before the person has seen what Pro does).
    const msLeft = freeProDayExpiresAt ? new Date(freeProDayExpiresAt).getTime() - Date.now() : 0;
    if (!freeProLeft || msLeft > 6 * 3_600_000) return null;
    return (
      <Box className="icon-text-row" sx={{
        background: 'rgba(255,215,64,0.06)',
        border: '1px solid rgba(255,215,64,0.4)',
        borderRadius: 1.5, px: 1.5, py: 1, mb: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
      }}>
        <Box className="icon-text-row" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box className="text-stack" sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: '#FFD740', lineHeight: 1.3 }}>
              Free Pro day · {freeProLeft} left
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.3 }}>
              Keep unlimited access when it ends.
            </Typography>
          </Box>
        </Box>
        <Button
          size="small"
          onClick={goUpgrade}
          sx={{ fontWeight: 700, textTransform: 'none', fontSize: '0.8rem', whiteSpace: 'nowrap', px: 1.5, flexShrink: 0 }}
        >
          Keep Pro
        </Button>
      </Box>
    );
  }

  if (variant === 'banner') {
    return (
      <Box sx={{
        background: 'rgba(0,224,255,0.05)',
        border: '1px solid rgba(0,224,255,0.3)',
        borderRadius: 1.5,
        px: 1.5, py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}>
        <Box className="icon-text-row" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BoltIcon sx={{ color: '#FFD740', fontSize: 18 }} />
          <Typography component="span" sx={{ fontSize: '0.8rem !important', mb: '0 !important', color: 'rgba(255,255,255,0.75)' }}>
            {message || defaultMessage}
          </Typography>
          {streakBonus > 0 && (
            <Chip label={`+${streakBonus} streak bonus`} size="small" sx={{ bgcolor: 'rgba(0,230,118,0.15)', color: '#00E676', fontSize: '0.7rem', height: 20 }} />
          )}
        </Box>
        <Button
          size="small"
          onClick={goUpgrade}
          variant="outlined"
          sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap', py: 0.5 }}
        >
          See Pro
        </Button>
      </Box>
    );
  }

  if (variant === 'inline') {
    return (
      <Card sx={{ ...cyberpunkCardSx, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <BoltIcon sx={{ color: '#FFD740', filter: 'drop-shadow(0 0 8px rgba(255,215,64,0.4))' }} />
          <Typography variant="subtitle2" sx={{ fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: 2, fontSize: '0.7rem', fontWeight: 700, color: '#FFD740' }}>
            Pro
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
          {message || defaultMessage}
        </Typography>
        {usageInfo && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{usageInfo.label}</Typography>
              <Typography variant="caption" sx={{ color: '#00E0FF', fontFamily: 'var(--font-mono)' }}>{usageInfo.used}/{usageInfo.limit}</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min((usageInfo.used / usageInfo.limit) * 100, 100)}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.05)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  background: usageInfo.used >= usageInfo.limit
                    ? '#FF1744'
                    : usageInfo.used >= usageInfo.limit * 0.8
                    ? '#FFD740'
                    : '#00E0FF',
                },
              }}
            />
          </Box>
        )}
        <Button
          fullWidth
          variant="contained"
          onClick={goUpgrade}
          sx={{
            background: 'linear-gradient(135deg, #00E0FF, #00E676)',
            color: '#0a0e14',
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
          }}
        >
          Start free trial
        </Button>
      </Card>
    );
  }

  // Modal variant
  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          ...cyberpunkCardSx,
          m: 2,
        },
      }}
    >
      <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
        <BoltIcon sx={{ fontSize: 36, color: '#FFD740', filter: 'drop-shadow(0 0 12px rgba(255,215,64,0.5))', mb: 1 }} />
        <Typography sx={{ color: '#FFD740', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: 2, fontWeight: 800, fontSize: '0.9rem', mb: 1 }}>
          {title || (isProFeature ? 'Pro feature' : 'Daily limit reached')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
          {message || defaultMessage}
        </Typography>
        {streakBonus > 0 && (
          <Chip
            label={`Your ${streakBonus > 0 ? `+${streakBonus}` : ''} streak bonus is already applied`}
            size="small"
            sx={{ bgcolor: 'rgba(0,230,118,0.15)', color: '#00E676', mb: 2 }}
          />
        )}
        {!isProFeature && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Pro takes the daily limits off.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
        <Button onClick={() => { track('nudge_dismissed', { variant, usageType }); onClose?.(); }} sx={{ color: 'rgba(255,255,255,0.6)' }}>
          Not now
        </Button>
        <Button
          variant="contained"
          onClick={() => { onClose?.(); goUpgrade(); }}
          sx={{
            background: 'linear-gradient(135deg, #00E0FF, #00E676)',
            color: '#0a0e14',
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            px: 3,
          }}
        >
          Start free trial
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeNudge;
